<template>
  <div class="trip-timeline-section">
    <h3 class="text-center main-ride-titles">{{ t('tripSummary') }}</h3>
    <!-- Desktop: the summary and the map side by side, the map open.
         Mobile: the summary full width, and the map button swaps it for the
         map in the same place. -->
    <div :class="sideBySide ? 'row' : 'classic-summary'">
      <div :class="sideBySide ? 'hotels-sumary centering_line_with_gap flex padding-right-1_5rem' : 'classic-summary-list'">
        <div class="centering_with_gap padding-right-1_5rem" >
          <div :class="['d-flex flex-wrap align-items-center gap-2 mt-3 padding-bottom-2', sideBySide ? 'classic-tabs-end' : 'justify-content-center']">
              <div
                @click="setHeading('activities')"
                :class="['clikable d-inline-flex align-items-center attraction-button-size primary fs-6 smaler-for-mobile',
                          selectedHeading === 'activities' ? 'activities-activ' : 'activities-default' ,
                          shouldBounce ? 'bounce' : ''
                ]"
              >
                {{ t('activities') }}
              </div>
              <div
                v-if="allRide.highLevelRideLocations.length"
                @click="setHeading('accommodations')"
                :class="['clikable d-inline-flex align-items-center attraction-button-size primary fs-6 smaler-for-mobile',
                          selectedHeading === 'accommodations' ? 'activities-activ' : 'activities-default'
                ]"
                >
                {{ t('accommodations') }}
              </div>
              <button
                v-if="!sideBySide"
                type="button"
                :class="['clikable d-inline-flex align-items-center attraction-button-size primary fs-6 smaler-for-mobile classic-view-toggle',
                          showMap ? 'activities-activ' : 'activities-default'
                ]"
                :aria-pressed="showMap ? 'true' : 'false'"
                @click="showMap = !showMap"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path fill="currentColor" d="M15 5.1 9 3 3 5v16l6-2.1 6 2.1 6-2V3l-6 2.1zM10 5.4l4 1.4v11.8l-4-1.4V5.4zM5 6.4l3-1v11.8l-3 1V6.4zm14 11.2-3 1V6.8l3-1v11.8z"/>
                </svg>
                {{ t('rideSummaryMap') }}
              </button>
          </div>

          <div v-show="sideBySide || !showMap">
          <div
            v-if="selectedHeading === 'activities'"
          >
            <RideDayList
              :days="allRide.attractionsByDay"
              :dayLabel="dayLabel"
              @open-day="scrollToRideDay"
            />
          </div>
          <ClassicStaysTimeline
            v-if="selectedHeading === 'accommodations'"
            :stays="allRide.highLevelRideLocations"
            @select="scrollToRideElement($event.id + '_acommodation_ref_id')"
          />
          </div>
        </div>
      </div>
      <!-- Desktop: the map's own column, with a hide / show button. -->
      <div v-if="sideBySide" class="hotels-sumary padding-left-1_5rem">
        <RideMapToggle v-model="showMap" inColumn/>
      </div>
      <!-- Mobile: created the first time it is shown, then only hidden and
           shown, so the map, its markers and its day areas survive every swap. -->
      <div
        v-else-if="mapCreated"
        v-show="showMap"
        class="classic-summary-map">
        <GlobalMap
          passed_class="ride_samery_map">
        </GlobalMap>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import GlobalMap from '@/components/GlobalMap';
import RideMapToggle from '@/components/RideSummary/RideMapToggle';
import ClassicStaysTimeline from './ClassicStaysTimeline';
import RideDayList from '@/components/RideSummary/RideDayList';
import { initialMapOpen } from '@/components/RideSummary/initialMapOpen';
import { rideDayDate } from "@/utils/rideDayLabels";
import { scrollToRideDay, scrollToRideElement } from "@/utils/scrollToRideElement";
import { drawRideDayAreas } from '@/components/RideSummary/drawRideDayAreas';

/**
 * The classic design's trip summary: an "activities" list of every day and an
 * "accommodations" timeline of the stays.
 *
 * The whole-ride map: on desktop in a column beside the summary, open; on
 * mobile a map button swaps the summary for the map, full width in the same
 * place. Either way it shows what the selected tab is about: the stays'
 * numbered markers, and with "activities" also one area per day.
 *
 * The map is created on its first showing and after that only hidden, never
 * rebuilt: GlobalMap's setAppMap replays the stay markers once, on creation
 * (globalMap.js thisMapWereCreated), and the day areas stay on it.
 */
export default {
  name: 'ClassicTimeline',
  mixins: [translationMixin],
  components: { GlobalMap, RideMapToggle, ClassicStaysTimeline, RideDayList },
  data() {
    // Decided once, like the store's isMobile: desktop starts on the map.
    const desktop = initialMapOpen(this.$store);
    return {
      selectedHeading: 'accommodations',
      shouldBounce: false,
      bounceInterval: null,
      sideBySide: desktop,
      showMap: desktop,
      mapCreated: desktop,
    };
  },
  computed: {
    ...mapGetters(['allRide', 'appMap']),
  },
  mounted() {
    this.openActivitiesForSingleStay();
    if (this.selectedHeading === 'activities') return;
    // Nudge towards the activities tab every 3 seconds until it is opened.
    this.bounceInterval = setInterval(() => {
      this.shouldBounce = true;
      setTimeout(() => {
        this.shouldBounce = false;
      }, 500); // match to the bounce animation duration
    }, 3000);
  },
  beforeDestroy() {
    clearInterval(this.bounceInterval);
  },
  watch: {
    showMap(visible) {
      if (!visible) return;
      if (!this.mapCreated) {
        // The appMap watcher draws the day areas once the new map is ready.
        this.mapCreated = true;
        return;
      }
      if (this.selectedHeading === 'activities') {
        this.$nextTick(() => this.showAllDayPolygonsOnMap());
      }
    },
    allRide() {
      this.openActivitiesForSingleStay();
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.selectedHeading === 'activities') {
            this.showAllDayPolygonsOnMap();
          }
        }, 300);
      });
    },
    selectedHeading(newHeading) {
      this.$nextTick(() => {
        if (newHeading === 'activities') {
          this.showAllDayPolygonsOnMap();
        } else {
          this.removeAllPolygonsFromGlobalMap();
        }
      });
    },
    appMap(newMap) {
      // The map was just created: draw the areas if activities are selected.
      if (newMap && this.selectedHeading === 'activities') {
        this.$nextTick(() => {
          this.showAllDayPolygonsOnMap();
        });
      }
    },
  },
  methods: {
    ...mapActions(['removeAllPolygonsFromGlobalMap']),
    scrollToRideDay,
    scrollToRideElement,
    // A trip with one stay has nothing to show on the stays timeline.
    openActivitiesForSingleStay() {
      if (this.allRide && this.allRide.highLevelRideLocations && this.allRide.highLevelRideLocations.length <= 1) {
        this.setHeading('activities');
      }
    },
    dayLabel(index) {
      const dayLabel = this.t('dayNumber', { number: index + 1 });
      const date = rideDayDate(this.allRide && this.allRide.ride, index);
      return date ? `${dayLabel} - ${date}` : dayLabel;
    },
    setHeading(heading) {
      if (heading === 'activities') {
        clearInterval(this.bounceInterval);
      }
      this.selectedHeading = heading;
    },
    showAllDayPolygonsOnMap() {
      // Only show activities on map when activities tab is selected
      if (this.selectedHeading !== 'activities') {
        return;
      }
      // Hidden map: drawn when it is shown (the showMap watcher), and before
      // it is first created appMap may be another page's map.
      if (!this.showMap || !this.mapCreated) {
        return;
      }

      drawRideDayAreas(this.$store, () => this.selectedHeading === 'activities' && this.showMap);
    },
  },
};
</script>

<style scoped>
.activities-default {
  opacity: 1;
  border-radius: 12px;
  box-shadow: inset 0 0 0 2px var(--primary-color, #07A0C3);
  background: white !important;
  color: var(--primary-color, #07A0C3) !important;
}
.activities-activ {
  opacity: 1;
  border-radius: 12px;
}

@media (max-width: 550px) {
  .smaler-for-mobile{
    font-size: 1.1rem!important;
    padding: 3px 7px!important;
  }
}

.clikable {
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 10px 20px;
  text-align: center;
}

.clikable:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
}

@keyframes bounce {
  0%   { transform: scale(1); }
  30%  { transform: scale(1.1); }
  50%  { transform: scale(0.95); }
  70%  { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.bounce {
  animation: bounce 0.5s ease;
}

.classic-summary {
  width: 100%;
}

.classic-summary-list {
  display: flex;
  justify-content: center;
}

.classic-tabs-end {
  justify-content: flex-end !important;
}

.classic-view-toggle {
  gap: 6px;
  border: 0;
}

/* general.css sizes .ride_samery_map for the old side column (86rem tall, an
   80px margin under RTL); here it has the summary's full width instead. */
.classic-summary-map ::v-deep .ride_samery_map {
  height: min(70vh, 620px) !important;
  max-height: none;
  margin-left: 0 !important;
}

.main-ride-titles {
  margin: 2rem 1.5rem !important;
  padding: 1rem 0 !important;
}

@media (max-width: 768px) {
  .main-ride-titles {
    font-size: 2.8rem !important; /* larger than day-number to maintain title hierarchy */
    margin: 1.5rem 1rem !important;
    padding: 0.75rem 0 !important;
  }
}

@media (max-width: 480px) {
  .main-ride-titles {
    font-size: 2.6rem !important; /* clearly larger than day-number (2.4rem) */
    margin: 1rem 0.75rem !important;
    padding: 0.5rem 0 !important;
  }
}
</style>
